import { useNavigate } from "react-router-dom";
import { Calendar } from "lucide-react";

const STATE_COLORS = {
  federal: "#0D5C3A", NSW: "#0284C7", VIC: "#334155", QLD: "#991B1B",
  WA: "#D97706", SA: "#E11D48", TAS: "#059669", ACT: "#2563EB", NT: "#C026D3",
};

const ELECTION_GUIDE_PATHS = {
  "sa-2026": "/sa-2026",
  "vic-2026": "/vic-2026",
  "nsw-2027": "/nsw-2027",
};

export function UpcomingElectionsGrid({ upcomingElections, isDark, surface, text, textSec, borderC, shadowMd }) {
  const navigate = useNavigate();

  const getDaysUntil = (d) => Math.ceil((new Date(d) - new Date()) / 864e5);
  const formatDate = (d) => new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

  return (
    <section className="py-14 px-4" data-testid="upcoming-elections-section" style={{ backgroundColor: isDark ? "#080E1A" : "#F8FAFC" }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(225,29,72,0.10)", border: "1px solid rgba(225,29,72,0.18)" }}>
            <Calendar style={{ width: 15, height: 15, color: "#E11D48" }} />
          </div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
            Upcoming Elections
          </h2>
        </div>

        {upcomingElections.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-2xl" style={{ background: isDark ? "rgba(30,41,59,0.5)" : "#E2E8F0" }} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingElections.map((election) => {
              const days = getDaysUntil(election.date);
              const stateColor = STATE_COLORS[election.state || "federal"];
              const isImminent = days <= 120;
              const guidePath = ELECTION_GUIDE_PATHS[election.id];
              return (
                <div
                  key={election.id}
                  data-testid={`election-card-${election.id}`}
                  onClick={() => { if (guidePath) navigate(guidePath); }}
                  className="transition-all duration-200 hover:-translate-y-1"
                  style={{
                    background: surface,
                    border: `1px solid ${isImminent ? `${stateColor}22` : borderC}`,
                    borderRadius: 16,
                    boxShadow: isImminent
                      ? `0 4px 20px -2px ${stateColor}18, ${isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(15,23,42,0.05)"}`
                      : shadowMd,
                    padding: "20px",
                    cursor: guidePath ? "pointer" : "default",
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ background: stateColor }}>
                      {election.state || "FEDERAL"}
                    </span>
                    {isImminent && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "rgba(225,29,72,0.10)", color: "#E11D48", border: "1px solid rgba(225,29,72,0.18)" }}>
                        COMING SOON
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-sm mb-1 tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: text }}>{election.name}</h3>
                  <p className="text-xs mb-4" style={{ color: textSec }}>{formatDate(election.date)}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-3xl font-black leading-none" style={{ fontFamily: "Outfit, sans-serif", color: isImminent ? "#E11D48" : "#0D5C3A" }}>{days}</div>
                      <div className="text-xs mt-0.5" style={{ color: textSec }}>days away</div>
                    </div>
                    {guidePath ? (
                      <span className="text-xs font-bold px-3 py-1.5 rounded-full transition-all" style={{ background: stateColor, color: "#fff" }} data-testid={`election-guide-link-${election.id}`}>
                        View Guide →
                      </span>
                    ) : (
                      <p className="text-xs max-w-[55%] text-right line-clamp-2" style={{ color: textSec }}>{election.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
